import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Empty } from 'antd'
import { AutoSizer, Grid } from 'react-virtualized'
import { useDispatch, useSelector } from 'react-redux'
import { produce } from 'immer'
import HeaderExtra from './components/HeaderExtra'
import Viewer from '../components/ViewerV2'
import Photo from '../components/Photo'
import backTop from '../../images/back_top.png'
import { trackPicsPageClickV2 } from './utils/track'
import { capturePromiseException } from '../../utils/sentryUtils'
import { setLoading } from '../../actions/main'
import { buildFetchPhotosParamsWithOptions } from './lib/photo'
import {
  fetchPhotosByPrecise,
  fetchMorePhotosByPrecise,
  pushCheckedPhoto,
  pushUncheckedPhoto,
  removeCheckedPhoto,
  removeUncheckedPhoto,
  selectPhotos as selectPrecisePhotos,
  selectPhotosNum as selectPrecisePhotosNum,
  selectLastPageCond as selectPreciseLastPageCond,
  selectCheckedPhotos as selectPreciseCheckedPhotos,
  selectUncheckedPhotos as selectPreciseUncheckedPhotos,
  selectCheckAll as selectPreciseCheckAll,
  selectPreciseSearch,
  selectIsAscSort as selectPreciseIsAscSort,
  setCheckAll as setPreciseCheckAll,
  clearCheck as clearPreciseCheck,
  setIsAscSort as setPreciseIsAscSort,
  setPhotos as setPrecisePhotos
} from '../../slices/photosByPreciseSlice'
import { selectSearchFilter } from '../../slices/photoFilterSlice'
import type { RootState } from '../../app/store'
import type { Photo as PhotoType } from './types'
import { normalizeToSet } from './utils/selectionHelpers'
import { getSensorContent } from '../../utils/photoUtils'

interface PhotosListPreciseProps {
  photoListRef: React.RefObject<any>
}

const PhotosListPrecise: React.FC<PhotosListPreciseProps> = ({
  photoListRef
}) => {
  const dispatch = useDispatch()
  const viewerRef = useRef<any>(null)
  const scrollFetchingRef = useRef(false)

  const [autoSizerWidth, setAutoSizerWidth] = useState(0)
  const [visibleBackTop, setVisibleBackTop] = useState(false)

  const photos = useSelector(selectPrecisePhotos)
  const photosNum = useSelector(selectPrecisePhotosNum)
  const lastPageCond = useSelector(selectPreciseLastPageCond)
  const checkedPhotosRaw = useSelector(selectPreciseCheckedPhotos)
  const unCheckedPhotosRaw = useSelector(selectPreciseUncheckedPhotos)
  const checkAll = useSelector(selectPreciseCheckAll)
  const preciseSearch = useSelector(selectPreciseSearch)
  const isAscSort = useSelector(selectPreciseIsAscSort)
  const queryFilter = useSelector(selectSearchFilter)
  const groupId = useSelector((state: RootState) => state.group.groupId)
  const myRole = useSelector((state: RootState) => state.group.myRole)

  const scrollGridToTop = useCallback(() => {
    if (photoListRef?.current?.scrollToPosition) {
      photoListRef.current.scrollToPosition({
        scrollLeft: 0,
        scrollTop: 0
      })
      return
    }

    if (photoListRef?.current?.scrollToRow) {
      photoListRef.current.scrollToRow(0)
      return
    }

    const gridDom = document.getElementsByClassName('virtualized-grid')[0] as
      | HTMLElement
      | undefined
    if (gridDom) {
      gridDom.scrollTop = 0
    }
  }, [photoListRef])

  const checkedPhotos = useMemo(
    () => normalizeToSet(checkedPhotosRaw),
    [checkedPhotosRaw]
  )
  const unCheckedPhotos = useMemo(
    () => normalizeToSet(unCheckedPhotosRaw),
    [unCheckedPhotosRaw]
  )

  useEffect(() => {
    return () => {
      dispatch(clearPreciseCheck())
    }
  }, [dispatch])

  const handleSensorOfClickEvent = useCallback(
    (clickItem: string, restProperties: Record<string, any> = {}) => {
      trackPicsPageClickV2({
        clickItem,
        isOpenErrorCorrection: queryFilter?.useSynonym,
        ...restProperties
      })
    },
    [queryFilter]
  )

  const handlePushCheckedPhoto = useCallback(
    (photo: PhotoType) => {
      handleSensorOfClickEvent('chooseOnePhoto')
      if (checkAll) {
        dispatch(removeUncheckedPhoto(photo.seq))
      } else {
        if (checkedPhotos.size === Math.max(photosNum - 1, 0)) {
          dispatch(setPreciseCheckAll())
        } else {
          dispatch(pushCheckedPhoto(photo.seq))
        }
      }
    },
    [checkAll, checkedPhotos, dispatch, handleSensorOfClickEvent, photosNum]
  )

  const handleRemoveCheckedPhoto = useCallback(
    (photo: PhotoType) => {
      handleSensorOfClickEvent('cancelChooseOnePhoto')
      if (checkAll) {
        if (unCheckedPhotos.size === Math.max(photosNum - 1, 0)) {
          dispatch(clearPreciseCheck())
        } else {
          dispatch(pushUncheckedPhoto(photo.seq))
        }
      } else {
        dispatch(removeCheckedPhoto(photo.seq))
      }
    },
    [
      checkAll,
      dispatch,
      handleSensorOfClickEvent,
      photosNum,
      unCheckedPhotos.size
    ]
  )

  const handleBackTopClick = useCallback(() => {
    const dom = document.getElementsByClassName(
      'virtualized-grid'
    )[0] as HTMLElement
    if (dom) {
      dom.scrollTop = 0
    }
  }, [])

  const handleFetchMore = useCallback(async () => {
    if (
      !lastPageCond ||
      scrollFetchingRef.current ||
      !queryFilter ||
      !groupId
    ) {
      return
    }

    scrollFetchingRef.current = true
    try {
      const params = buildFetchPhotosParamsWithOptions({
        queryFilter,
        groupID: groupId,
        lastPageCond,
        isPreciseSearch: true,
        isAscSort,
        // 跟 PhotosList / handleFilterSubmit 对齐:locationRecommend 必须透传,
        // 否则 AI apply_page_filter 推 location_key 后精确搜索 tab 也会丢条件。
        locationRecommend: queryFilter.locationRecommend ?? null
      })
      await dispatch(fetchMorePhotosByPrecise(params) as any)
    } catch (error) {
      capturePromiseException(error as Error)
    } finally {
      scrollFetchingRef.current = false
    }
  }, [dispatch, groupId, lastPageCond, queryFilter, isAscSort])

  const handleGridScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }) => {
      if (scrollTop > window.screen.height - 210) {
        setVisibleBackTop(true)
      } else {
        setVisibleBackTop(false)
      }

      if (
        !scrollFetchingRef.current &&
        parseInt(
          String(
            scrollHeight > clientHeight
              ? scrollHeight - clientHeight - scrollTop
              : 0
          )
        ) <= 0
      ) {
        handleFetchMore()
      }
    },
    [handleFetchMore]
  )

  const handleSortChange = useCallback(
    async (nextIsAscSort: boolean) => {
      if (!queryFilter || !groupId) return
      dispatch(setPreciseIsAscSort(nextIsAscSort))
      dispatch(clearPreciseCheck())
      dispatch(setLoading(true))
      try {
        const params = buildFetchPhotosParamsWithOptions({
          queryFilter,
          groupID: groupId,
          isAscSort: nextIsAscSort,
          isPreciseSearch: true,
          lastPageCond: '',
          // 同上:排序变化也要保留 locationRecommend,否则 AI 推过来的"北京"会丢。
          locationRecommend: queryFilter.locationRecommend ?? null
        })
        await dispatch(fetchPhotosByPrecise(params) as any)
      } catch (error) {
        capturePromiseException(error as Error)
      } finally {
        dispatch(setLoading(false))
      }
      scrollGridToTop()
    },
    [dispatch, groupId, queryFilter, scrollGridToTop]
  )

  const renderCell = useCallback(
    ({
      columnIndex,
      rowIndex,
      style,
      columnCount
    }: {
      columnIndex: number
      rowIndex: number
      style: React.CSSProperties
      columnCount: number
    }) => {
      const index = columnCount * rowIndex + columnIndex
      const photo = photos[index]

      if (!photo) return null

      const isChecked = checkAll
        ? !unCheckedPhotos.has(photo.seq)
        : checkedPhotos.has(photo.seq)

      return (
        <div
          style={{ margin: '0 auto', ...style }}
          key={`${rowIndex}-${index}`}
        >
          <Photo
            style={{ margin: 0 }}
            photo={photo as any}
            canCheck
            readyToCheck={
              checkedPhotos.size > 0 || unCheckedPhotos.size > 0 || checkAll
            }
            checked={isChecked}
            onPreview={() => {
              handleSensorOfClickEvent('photo', {
                photoDateTime: photo.time || '',
                PassiveUserid: photo.userID || '',
                tabName: '2',
                imageUrl: photo.largeURL,
                number3: photo.createdAt,
                string6: photo.seq,
                string7: photo.time,
                popType: getSensorContent(photo.sourceType)
              })
              if (viewerRef.current) {
                viewerRef.current.view(index)
              }
            }}
            onCheck={() => handlePushCheckedPhoto(photo)}
            onUnCheck={() => handleRemoveCheckedPhoto(photo)}
            track={(clickItem, restProperties) => {
              trackPicsPageClickV2({
                clickItem,
                isOpenErrorCorrection: queryFilter?.useSynonym,
                ...restProperties
              })
            }}
          />
        </div>
      )
    },
    [
      checkAll,
      checkedPhotos,
      groupId,
      handlePushCheckedPhoto,
      handleRemoveCheckedPhoto,
      handleSensorOfClickEvent,
      myRole,
      photos,
      queryFilter?.useSynonym,
      unCheckedPhotos
    ]
  )

  const handlePhotoChange = useCallback(
    (seq: number, favorite: boolean) => {
      dispatch(
        setPrecisePhotos(
          produce(photos, (draft) => {
            const index = draft.findIndex((photo) => photo.seq === seq)
            if (index !== -1) {
              draft[index].isFavorite = favorite
            }
          })
        )
      )
    },
    [dispatch, photos]
  )

  useEffect(() => {
    if (photos.length === 0 && photoListRef?.current) {
      photoListRef.current.scrollToPosition({
        scrollLeft: 0,
        scrollTop: 0
      })
    }
  }, [photos.length, photoListRef])

  const suggestedWords =
    queryFilter && Array.isArray(queryFilter.suggestedSearchWords)
      ? queryFilter.suggestedSearchWords
      : []
  const hasSuggestedSearchWords = suggestedWords.length > 0

  const notWords =
    preciseSearch?.notWords?.map((word) => `“${word}”`).join('、') || ''

  const columnWidth = 238
  const useSimpleFillLayout = photos.length > 0 && photos.length <= 4

  return (
    <>
      <div className="photoList">
        <HeaderExtra
          dataSource="precise"
          onSortChange={handleSortChange}
          isAsc={isAscSort}
        />
        {hasSuggestedSearchWords && notWords && (
          <div style={{ color: '#848484', margin: '10px auto' }}>
            <span style={{ color: '#EA4D3D', marginRight: 10 }}>*</span>
            已去除{notWords}等非精准结果
          </div>
        )}
        {useSimpleFillLayout ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${photos.length}, minmax(208px, 1fr))`,
              gap: '20px',
              alignItems: 'start',
              width: '100%',
              paddingTop: '12px'
            }}
          >
            {photos.map((photo, photoIndex) => {
              const isChecked = checkAll
                ? !unCheckedPhotos.has(photo.seq)
                : checkedPhotos.has(photo.seq)

              return (
                <div
                  key={`precise-simple-photo-${photo.seq}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0
                  }}
                >
                  <Photo
                    style={{ margin: 0 }}
                    photo={photo as any}
                    canCheck
                    checked={isChecked}
                    readyToCheck={
                      checkedPhotos.size > 0 ||
                      unCheckedPhotos.size > 0 ||
                      checkAll
                    }
                    onPreview={() => {
                      if (viewerRef.current) {
                        viewerRef.current.view(photoIndex)
                      }
                    }}
                    onCheck={() => handlePushCheckedPhoto(photo)}
                    onUnCheck={() => handleRemoveCheckedPhoto(photo)}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <AutoSizer onResize={({ width }) => setAutoSizerWidth(width)}>
            {({ width, height }) => {
              const columnCount = Math.max(1, Math.floor(width / columnWidth))
              const rowCount = Math.ceil(photos.length / columnCount)
              return (
                <Grid
                  className="virtualized-grid"
                  ref={photoListRef}
                  style={{ outline: 'none', scrollBehavior: 'smooth' }}
                  width={width}
                  height={height}
                  columnWidth={columnWidth}
                  rowHeight={280}
                  columnCount={columnCount}
                  rowCount={rowCount}
                  cellRenderer={({ columnIndex, rowIndex, style }) =>
                    renderCell({
                      columnIndex,
                      rowIndex,
                      style,
                      columnCount
                    })
                  }
                  onScroll={handleGridScroll}
                />
              )
            }}
          </AutoSizer>
        )}
        <div
          className="isTop"
          style={{ opacity: visibleBackTop ? '1' : '0', right: '56px' }}
        >
          <img
            style={{ width: '48px' }}
            src={backTop}
            onClick={handleBackTopClick}
            alt=""
          />
        </div>
        {!photos.length && (
          <div className="empty-wrapper">
            <Empty description="无照片" />
          </div>
        )}
      </div>
      <Viewer
        ref={viewerRef}
        photos={photos}
        onFetchMore={handleFetchMore}
        canFavorite={true}
        showWatermarkAction={true}
        isShowComments={true}
        fromPlace="precisePhoto"
        // @ts-expect-error afterFavoriteChange 非 ViewerProps 标准属性
        afterFavoriteChange={handlePhotoChange}
      />
    </>
  )
}

export default PhotosListPrecise
